import { Component, FC, MouseEvent, ReactNode } from 'react';
import { RelayProp, graphql, createFragmentContainer } from 'react-relay';
import { connect, DispatchProp } from 'react-redux';
import { localStorage } from 'dibs-browser-storage';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button';
import ArrowRight from 'dibs-icons/exports/ArrowRight';
import makeOfferLauncher from 'dibs-buyer-layout/exports/makeOfferLauncher';

// mutations
import { SharedModifyPortfolioItems } from '../../mutations/FindingSharedModifyPortfolioItems';

// styles
import styles from './PdpSharedButtons.scss';

// helpers
import { Maybe } from 'dibs-ts-utils/exports/Maybe';
import { trackMakeOfferClick, trackMakeOfferFromEmail } from '../tracking/actionButtonsTracking';
import { setCartItems } from '../../actions/sharedMobileActions';
import { getQueryParam } from '../../utils/uriUtils';

import { PdpSharedMakeOfferButton_item$data } from './__generated__/PdpSharedMakeOfferButton_item.graphql';
import { PdpSharedMakeOfferButton_user$data } from './__generated__/PdpSharedMakeOfferButton_user.graphql';
// constants
const OPEN_OFFER_MODAL = 'openOfferModal';

type Props = {
    userId?: string | null;
    itemId: string;
    selectedSkuId?: string;
    item: PdpSharedMakeOfferButton_item$data;
    currency: string;
    relay: RelayProp;
    user: PdpSharedMakeOfferButton_user$data | null;
    size: 'small' | 'medium' | 'large';
    fullWidth?: boolean;
    quantitySelected?: string;
    showArrow: boolean;
    hasPrivateOffer?: Maybe<boolean>;
    hasShortenedCta?: Maybe<boolean>;
    shouldLaunchContactDealer?: boolean;
    gaLabel?: string;
} & DispatchProp;

const makeOfferCta = (
    <FormattedMessage id="PdpSharedMakeOfferButton.makeOfferCta" defaultMessage="Make An Offer" />
);

const makeOfferShortenedCta = (
    <FormattedMessage
        id="PdpSharedMakeOfferButton.makeOfferShortenedCta"
        defaultMessage="Make Offer"
    />
);

const MakeOfferCTA: FC<{ showArrow?: Maybe<boolean>; hasShortenedCta?: Maybe<boolean> }> = ({
    showArrow,
    hasShortenedCta,
}) => (
    <div className={styles.makeOfferButton}>
        {hasShortenedCta ? makeOfferShortenedCta : makeOfferCta}
        {showArrow && <ArrowRight className={styles.arrowSVG} />}
    </div>
);

class PdpSharedMakeOfferButtonComponentClass extends Component<Props> {
    static defaultProps = {
        size: 'large',
        showArrow: true,
        hasPrivateOffer: false,
        shouldLaunchContactDealer: true,
        fullWidth: true,
    };

    constructor(props: Props) {
        super(props);

        this.openModal = this.openModal.bind(this);
    }

    componentDidMount(): void {
        const queryParams = global?.location?.search;
        const modalParam = getQueryParam(queryParams, 'modal');
        const templateParam = getQueryParam(queryParams, 'utm_campaign');
        const openModalFromRefresh = localStorage.getItem(OPEN_OFFER_MODAL);

        if (modalParam === 'offer' || openModalFromRefresh) {
            localStorage.deleteItem(OPEN_OFFER_MODAL);
            this.openModal();

            if (templateParam) {
                trackMakeOfferFromEmail(templateParam, {
                    pdpTrackingVpvParams: this.props.item.makeOfferVpvParams,
                });
            }
        }
    }

    openModal(event?: MouseEvent): void {
        const {
            dispatch,
            userId,
            itemId,
            selectedSkuId,
            relay,
            quantitySelected,
            currency,
            hasPrivateOffer,
            shouldLaunchContactDealer,
            gaLabel,
        } = this.props;
        const { makeOfferVpvParams: pdpTrackingVpvParams, ...item } = this.props.item;
        trackMakeOfferClick({ ...item, pdpTrackingVpvParams }, hasPrivateOffer, event);

        if (!userId) {
            // If no user, store item - it can be ADDed to cart when user logs in later
            dispatch(setCartItems(itemId));
        } else {
            // ADD item to user's cart
            this.addToCartMutation(itemId);
        }

        makeOfferLauncher({
            itemId,
            selectedSkuId,
            currency,
            environment: relay.environment,
            quantitySelected,
            shouldLaunchContactDealer,
            launchOrigin: 'pdp-shared',
            gaLabel,
        });
    }

    addToCartMutation(itemId: string): void {
        const { relay, userId } = this.props;
        SharedModifyPortfolioItems.commit(relay.environment, {
            action: 'ADD',
            itemId,
            portfolioType: 'CART',
            userId,
        });
    }

    render(): ReactNode {
        const { size, fullWidth, showArrow, item, hasShortenedCta } = this.props;
        const isPrivateVisibilityType = item?.isPrivateVisibilityType;
        return (
            <Button
                type={isPrivateVisibilityType ? 'secondaryAlt' : 'secondary'}
                className={styles.button}
                dataTn="item-actions-make-offer"
                onClick={this.openModal}
                size={size}
                fullWidth={fullWidth}
            >
                <MakeOfferCTA
                    showArrow={showArrow && size !== 'small'}
                    hasShortenedCta={hasShortenedCta}
                />
            </Button>
        );
    }
}

export const PdpSharedMakeOfferButtonComponent = connect()(PdpSharedMakeOfferButtonComponentClass);

export const PdpSharedMakeOfferButton = createFragmentContainer(PdpSharedMakeOfferButtonComponent, {
    item: graphql`
        fragment PdpSharedMakeOfferButton_item on Item {
            ...SharedContactDealerWrapper_item
            ...actionButtonsTracking_item @arguments(fetchAuction: $fetchAuction)
            makeOfferVpvParams: pdpTrackingVpvParams(
                userId: $userId
                excludedPurchaseStatuses: [RET, CUST]
            ) @include(if: $isClient)
            isPrivateVisibilityType
        }
    `,
    user: graphql`
        fragment PdpSharedMakeOfferButton_user on User {
            ...SharedContactDealerWrapper_user
            preferences {
                currency
            }
        }
    `,
});
